import { ChangeEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, type FieldValues, type FieldPathValue, type FieldPath } from 'react-hook-form';
import Box from '@mui/material/Box';
import Input from '@mui/material/Input';
import Select from '@mui/material/Select';
import { FieldControlInputContainer } from '../_components/FieldControlInputContainer/FieldControlInputContainer';
import { FieldControlLabelContainer } from '../_components/FieldControlLabelContainer/FieldControlLabelContainer';
import { ISelectFieldControlProps } from './SelectFieldControl.interface';

export const SelectFieldControl = <T extends FieldValues, TName extends FieldPath<T>, >(props: ISelectFieldControlProps<T, TName>) => {
  const {
    name, label, tooltip, action, readOnly, required, forceDisabled, multiple, children, mixedValues, bulkEdit, renderValue,
    onChange, revertValue,
  } = props;
  const { t } = useTranslation();

  const {
    field: { disabled, onChange: onFieldChange, ref, ...restFieldProps },
    fieldState: { invalid, error },
  } = useController<T, TName>({ name, disabled: forceDisabled, defaultValue: null as FieldPathValue<T, TName> });

  const isInputDisabled = disabled || readOnly;

  const handleChange = useCallback((event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onFieldChange(event);
    onChange?.(event.target.value as FieldPathValue<T, TName>);
  }, [onChange, onFieldChange]);

  const isValueEmpty = restFieldProps.value === undefined
    || restFieldProps.value === null
    || restFieldProps.value === ''
    || (Array.isArray(restFieldProps.value) && restFieldProps.value.length === 0);
  const showMixedPlaceholder = Boolean(mixedValues) && isValueEmpty;

  const resolvedRenderValue = showMixedPlaceholder
    ? () => (
      <Box
        component={'span'}
        sx={{
          fontStyle: 'italic',
          color: '#00897B',
        }}
      >
        {t('text:common.bulkEdit.mixedValues')}
      </Box>
    )
    : renderValue;

  return (
    <FieldControlLabelContainer
      label={label}
      tooltip={tooltip}
      htmlFor={name}
      disabled={isInputDisabled}
      error={invalid}
      required={required && !isInputDisabled}
    >
      <FieldControlInputContainer
        name={name}
        errorMessage={error?.message}
        action={action}
        bulkEdit={bulkEdit}
        revertValue={revertValue}
      >
        <Select
          id={name}
          value={restFieldProps.value}
          multiple={multiple}
          displayEmpty={showMixedPlaceholder}
          renderValue={resolvedRenderValue}
          sx={{
            '& .MuiSelect-icon': {
              right: '1px',
            },
          }}
          input={
            <Input
              {...restFieldProps}
              inputRef={ref}
              onChange={handleChange}
              id={name}
              disabled={isInputDisabled}
              error={invalid}
              fullWidth={true}
            />}
        >
          {children}
        </Select>
      </FieldControlInputContainer>
    </FieldControlLabelContainer>
  );
};
